<script lang="ts">
	import { format } from 'svelte-i18n';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import SettingGuide from '../../SettingGuide.svelte';
	import { Container, InputV2, Text, TokenV2, ToggleV2 } from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { get_gen_2_component_channel_structure } from 'src/helpers/interface_helpers';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import {
		cloneAlarmGroupState,
		EMETER_ALARM_PHASES,
		EMETER_ALARM_RANGES,
		EMETER_ALARM_VARIABLES,
		getEmeterSetConfigMethod,
		isEmeterEm1,
		parseAlarmGroup,
		serializeAlarmGroup,
		type EmeterAlarmGroupState,
		type EmeterAlarmPhase,
		type EmeterAlarmVariable,
	} from 'src/helpers/settings/emeter_settings_helpers';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import { getErrorOrInfoMessageForMinAndMax } from 'src/helpers/utilities';
	import { gen2Request } from 'src/services/gen_2_http_reqs';

	export let device: Device;

	const settings = readDeviceSettings(device.id);
	const configKey = get_gen_2_component_channel_structure(device);
	const method = getEmeterSetConfigMethod(device);
	const isEm1 = isEmeterEm1(device);
	const configuredAlarms = settings?.[configKey]?.alarms;

	let selectedPhase: EmeterAlarmPhase = 'a';
	let em1Alarms: EmeterAlarmGroupState = parseAlarmGroup(configuredAlarms);
	let phaseAlarms: Record<EmeterAlarmPhase, EmeterAlarmGroupState> = {
		a: parseAlarmGroup(configuredAlarms?.a),
		b: parseAlarmGroup(configuredAlarms?.b),
		c: parseAlarmGroup(configuredAlarms?.c),
	};
	let savedEm1 = JSON.stringify(serializeAlarmGroup(em1Alarms));
	let savedPhases = JSON.stringify({
		a: serializeAlarmGroup(phaseAlarms.a),
		b: serializeAlarmGroup(phaseAlarms.b),
		c: serializeAlarmGroup(phaseAlarms.c),
	});
	let loading = false;

	$: currentGroup = isEm1 ? em1Alarms : phaseAlarms[selectedPhase];
	$: isDirty = isEm1
		? JSON.stringify(serializeAlarmGroup(em1Alarms)) !== savedEm1
		: JSON.stringify({
				a: serializeAlarmGroup(phaseAlarms.a),
				b: serializeAlarmGroup(phaseAlarms.b),
				c: serializeAlarmGroup(phaseAlarms.c),
			}) !== savedPhases;
	$: hasError = (isEm1 ? [em1Alarms] : EMETER_ALARM_PHASES.map((phase) => phaseAlarms[phase])).some(
		(group) =>
			EMETER_ALARM_VARIABLES.some(
				(variable) =>
					!!sideError(group, variable, 'under') || !!sideError(group, variable, 'over'),
			),
	);
	$: saveDisabled = loading || !isDirty || hasError;

	const phaseLabel = (phase: EmeterAlarmPhase): string =>
		$format(`label_phase_${phase}`, { default: `Phase ${phase}` });

	const variableTitle = (variable: EmeterAlarmVariable): string => {
		if (variable === 'current') {
			return $format('settings_heading_emeter_alarm_current', { default: 'Current' });
		}
		if (variable === 'power') {
			return $format('settings_heading_emeter_alarm_power', { default: 'Power' });
		}
		return $format('settings_heading_emeter_alarm_voltage', { default: 'Voltage' });
	};

	const variableDescription = (variable: EmeterAlarmVariable): string => {
		if (variable === 'current') {
			return $format('settings_guide_emeter_alarm_current', {
				default:
					'Warn when current goes under or over these amps. This does not trip an output.',
			});
		}
		if (variable === 'power') {
			return $format('settings_guide_emeter_alarm_power', {
				default: 'Warn when power goes under or over these watts. This does not trip an output.',
			});
		}
		return $format('settings_guide_emeter_alarm_voltage', {
			default: 'Warn when voltage goes under or over these volts. This does not trip an output.',
		});
	};

	const parseSideValue = (value: string): number => (value === '' ? NaN : Number(value));

	const sideError = (
		group: EmeterAlarmGroupState,
		variable: EmeterAlarmVariable,
		side: 'under' | 'over',
	): string => {
		const state = group[variable];
		if (!state.enabled || !state[side].enabled) return '';
		const range = EMETER_ALARM_RANGES[variable];
		return getErrorOrInfoMessageForMinAndMax(
			parseSideValue(state[side].value),
			range.min,
			range.max,
			variable === 'power',
		).error;
	};

	const sideInfo = (
		group: EmeterAlarmGroupState,
		variable: EmeterAlarmVariable,
		side: 'under' | 'over',
	): string => {
		const state = group[variable];
		if (!state.enabled || !state[side].enabled) return '';
		const range = EMETER_ALARM_RANGES[variable];
		return getErrorOrInfoMessageForMinAndMax(
			parseSideValue(state[side].value),
			range.min,
			range.max,
			variable === 'power',
		).info;
	};

	const updateGroup = (next: EmeterAlarmGroupState) => {
		if (isEm1) {
			em1Alarms = cloneAlarmGroupState(next);
			return;
		}
		phaseAlarms = {
			...phaseAlarms,
			[selectedPhase]: cloneAlarmGroupState(next),
		};
	};

	const save = async () => {
		if (saveDisabled) return;

		const config = isEm1
			? { alarms: serializeAlarmGroup(em1Alarms) }
			: {
					alarms: {
						a: serializeAlarmGroup(phaseAlarms.a),
						b: serializeAlarmGroup(phaseAlarms.b),
						c: serializeAlarmGroup(phaseAlarms.c),
					},
				};

		loading = true;
		const res: SettingRequest = await gen2Request(device, method, {
			id: device.channel ?? 0,
			config,
		});
		if (res?.isok) {
			if (isEm1) {
				savedEm1 = JSON.stringify(serializeAlarmGroup(em1Alarms));
			} else {
				savedPhases = JSON.stringify({
					a: serializeAlarmGroup(phaseAlarms.a),
					b: serializeAlarmGroup(phaseAlarms.b),
					c: serializeAlarmGroup(phaseAlarms.c),
				});
			}
		}
		onSettingsResponse(res, device);
		loading = false;
	};

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		setStickyBottomAction({
			onClick: save,
			loading,
			disabled: saveDisabled,
			icon: 'fas fa-save',
		});
	}
</script>

<div class="emeter-alarms">
	<SettingGuide
		text={$format('settings_guide_emeter_alarms', {
			default:
				'Set under and over thresholds for voltage, current, and power. These flags do not switch an output.',
		})}
	/>

	{#if !isEm1}
		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_heading_emeter_alarm_phase', {
					default: 'Phase',
				})}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('settings_guide_emeter_alarm_phase', {
					default: 'Choose which phase these alarm thresholds apply to.',
				})}
			</Text>
			<div class="d-flex flex-wrap">
				{#each EMETER_ALARM_PHASES as phase (phase)}
					<TokenV2
						id={`token-emeter-alarm-phase-${phase}`}
						selected={selectedPhase === phase}
						size="m"
						title={phaseLabel(phase)}
						class="mr-s mb-s"
						aria-label={phaseLabel(phase)}
						on:click={() => {
							selectedPhase = phase;
						}}
					/>
				{/each}
			</div>
		</Container>
	{/if}

	{#each EMETER_ALARM_VARIABLES as variable (variable)}
		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">{variableTitle(variable)}</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{variableDescription(variable)}
			</Text>
			<ToggleV2
				size="m"
				class="mb-s"
				checked={currentGroup[variable].enabled}
				id={`toggle-emeter-alarm-${isEm1 ? 'em1' : selectedPhase}-${variable}`}
				aria-label={$format('label_enable', { default: 'Enable' })}
				on:click={() => {
					const next = cloneAlarmGroupState(currentGroup);
					next[variable].enabled = !next[variable].enabled;
					updateGroup(next);
				}}
			>
				{$format('label_enable', { default: 'Enable' })}
			</ToggleV2>
			{#if !currentGroup[variable].enabled}
				<Text styleType="body-small" class="mb-s text-color-muted">
					{$format('settings_guide_emeter_alarm_disabled', {
						default: 'Thresholds are ignored until this alarm is enabled.',
					})}
				</Text>
			{/if}

			<ToggleV2
				size="m"
				class="mb-s"
				checked={currentGroup[variable].under.enabled}
				disabled={!currentGroup[variable].enabled}
				id={`toggle-emeter-alarm-under-${isEm1 ? 'em1' : selectedPhase}-${variable}`}
				aria-label={$format('settings_label_emeter_alarm_under', {
					default: 'Under',
				})}
				on:click={() => {
					if (!currentGroup[variable].enabled) return;
					const next = cloneAlarmGroupState(currentGroup);
					next[variable].under.enabled = !next[variable].under.enabled;
					updateGroup(next);
				}}
			>
				{$format('settings_label_emeter_alarm_under', { default: 'Under' })}
			</ToggleV2>
			<InputV2
				{...getNumberInputConfig(true)}
				id={`input-emeter-alarm-under-${isEm1 ? 'em1' : selectedPhase}-${variable}`}
				aria-label={$format('settings_label_emeter_alarm_under', { default: 'Under' })}
				icon={null}
				size="m"
				width="full"
				class="mb-s"
				min={EMETER_ALARM_RANGES[variable].min}
				max={EMETER_ALARM_RANGES[variable].max}
				step={EMETER_ALARM_RANGES[variable].step}
				disabled={!currentGroup[variable].enabled || !currentGroup[variable].under.enabled}
				value={currentGroup[variable].under.value}
				info={sideInfo(currentGroup, variable, 'under')}
				error={sideError(currentGroup, variable, 'under')}
				on:input={(event) => {
					const next = cloneAlarmGroupState(currentGroup);
					next[variable].under.value = (event.target as HTMLInputElement)?.value ?? '';
					updateGroup(next);
				}}
			/>

			<ToggleV2
				size="m"
				class="mb-s"
				checked={currentGroup[variable].over.enabled}
				disabled={!currentGroup[variable].enabled}
				id={`toggle-emeter-alarm-over-${isEm1 ? 'em1' : selectedPhase}-${variable}`}
				aria-label={$format('settings_label_emeter_alarm_over', {
					default: 'Over',
				})}
				on:click={() => {
					if (!currentGroup[variable].enabled) return;
					const next = cloneAlarmGroupState(currentGroup);
					next[variable].over.enabled = !next[variable].over.enabled;
					updateGroup(next);
				}}
			>
				{$format('settings_label_emeter_alarm_over', { default: 'Over' })}
			</ToggleV2>
			<InputV2
				{...getNumberInputConfig(true)}
				id={`input-emeter-alarm-over-${isEm1 ? 'em1' : selectedPhase}-${variable}`}
				aria-label={$format('settings_label_emeter_alarm_over', { default: 'Over' })}
				icon={null}
				size="m"
				width="full"
				min={EMETER_ALARM_RANGES[variable].min}
				max={EMETER_ALARM_RANGES[variable].max}
				step={EMETER_ALARM_RANGES[variable].step}
				disabled={!currentGroup[variable].enabled || !currentGroup[variable].over.enabled}
				value={currentGroup[variable].over.value}
				info={sideInfo(currentGroup, variable, 'over')}
				error={sideError(currentGroup, variable, 'over')}
				on:input={(event) => {
					const next = cloneAlarmGroupState(currentGroup);
					next[variable].over.value = (event.target as HTMLInputElement)?.value ?? '';
					updateGroup(next);
				}}
			/>
		</Container>
	{/each}
</div>

<style>
	.emeter-alarms {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
	}
</style>
